import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { companyInfo } from "../../CompanyInfo/CompanyInfo";
import "./contact.css";
import {
  Mail, Phone, MapPin, Globe, Zap, Clock, CheckCircle2, ShieldCheck, Send
} from "lucide-react";

const contactDetails = [
  { icon: "Mail", label: "Email Us", value: "info@eduitalent.com", sub: "We reply within 24 hours", color: "#7c3aed" },
  { icon: "Phone", label: "India Office", value: "+91-8376913569", sub: "Mon–Sat, 10AM – 7PM IST", color: "#db2777" },
  { icon: "MapPin", label: "Noida, India", value: "2106A, 21st Floor, The iConic Corenthum Tower, Sector 62 Noida 201309", sub: "Headquarters", color: "#d97706" },
  { icon: "MapPin", label: "Dubai", value: "Al Basma Business Centre, Kazim Building - Office 101, Garhoud", sub: "Gulf Office", color: "#2563eb" },
];

const faqs = [
  { q: "How quickly do you respond?", a: "We aim to reply to all enquiries within 24 hours on business days. For urgent matters, call us directly." },
  { q: "Do you work with startups?", a: "Absolutely. We work with businesses of all sizes — from early-stage startups to large enterprises across India and the UAE." },
  { q: "Can I book a free discovery call?", a: "Yes! Just fill in the form and select 'Discovery Call' as the subject. Our team will schedule a free 20-minute call at your convenience." },
  { q: "Do you offer remote services?", a: "Yes — the majority of our services are available remotely via video call, with in-person options available in Noida and Globe." },
];

const iconMap = {
  Mail: <Mail size={24} />,
  Phone: <Phone size={24} />,
  MapPin: <MapPin size={24} />,
};

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", service: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [focused, setFocused] = useState("");
  const isStandalone = window.location.pathname === "/contact";

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setError("");

    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      title: formData.subject,
      service: formData.service,
      message: formData.message,
    };

    emailjs
      .send(
        "service_duqw3gn",        // Service ID
        "template_fho005r",       // Template ID: Contact Us
        templateParams,
        "ZcENBppJcdDAWQUXO"         // ← replace with your Public Key
      )
      .then(() => {
        setSubmitted(true);
        setSending(false);
      })
      .catch((err) => {
        console.error("EmailJS error:", err);
        setError("Something went wrong. Please try again or email us directly.");
        setSending(false);
      });
  };

  return (
    <div className={`cp-page ${isStandalone ? 'standalone-page' : ''}`}>

      {/* ── BACKGROUNDS ── */}
      <div className="cp-bg-photo">
        <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1400" alt="" />
        <div className="cp-bg-dim"></div>
      </div>
      <div className="cp-radials"></div>
      <div className="cp-blob cp-blob-1"></div>
      <div className="cp-blob cp-blob-2"></div>
      <div className="cp-blob cp-blob-3"></div>
      <div className="cp-blob cp-blob-4"></div>

      {/* Grid lines */}
      <div className="cp-grid-lines">
        {[...Array(5)].map((_, i) => <div key={i} className={`cp-vl cp-vl-${i + 1}`}></div>)}
        {[...Array(4)].map((_, i) => <div key={i} className={`cp-hl cp-hl-${i + 1}`}></div>)}
      </div>

      {/* Floating bubbles */}
      <div className="cp-bubbles">
        <div className="cp-bubble cp-b1" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Phone size={12} /> Call Us</div>
        <div className="cp-bubble cp-b2" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Mail size={12} /> Email Us</div>
        <div className="cp-bubble cp-b3" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Globe size={12} /> India &amp; UAE</div>
        <div className="cp-bubble cp-b4" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Zap size={12} /> 24hr Reply</div>
      </div>

      <div className="cp-container">

        {/* ── HERO HEADER ── */}
        <div className="cp-hero">
          <div className="cp-hero-badge">✦ Let's Connect</div>
          <h1 className="cp-hero-title">
            Start Your <span className="cp-accent">Growth Journey</span><br />Today
          </h1>
          <p className="cp-hero-sub">
            Whether you have a question, a project, or just want to explore — we're here. Drop us a message and we'll get back to you within 24 hours.
          </p>
          <div className="cp-hero-stats">
            <div className="cp-hstat"><span>500+</span><p>Clients Helped</p></div>
            <div className="cp-hstat-div"></div>
            <div className="cp-hstat"><span>24hr</span><p>Response Time</p></div>
            <div className="cp-hstat-div"></div>
            <div className="cp-hstat"><span>2</span><p>Countries</p></div>
          </div>
        </div>

        {/* ── CONTACT CARDS ── */}
        <div className="cp-cards-strip">
          {contactDetails.map((c, i) => (
            <div key={i} className="cp-card" style={{ "--cc": c.color }}>
              <div className="cp-card-orb"></div>
              <div className="cp-card-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>{iconMap[c.icon]}</div>
              <div className="cp-card-label">{c.label}</div>
              <div className="cp-card-value">{c.value}</div>
              <div className="cp-card-sub">{c.sub}</div>
              <div className="cp-card-line"></div>
            </div>
          ))}
        </div>

        {/* ── MAIN GRID ── */}
        <div className="cp-main-grid">

          {/* LEFT — info + photos + FAQ */}
          <div className="cp-left">

            {/* Office photos */}
            <div className="cp-photos">
              <div className="cp-photo-main">
                <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600" alt="Team" />
                <div className="cp-photo-badge" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <MapPin size={12} /> Noida HQ
                </div>
              </div>
              <div className="cp-photo-side">
                <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400" alt="Globe Office" />
                <div className="cp-photo-badge" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <MapPin size={12} /> Globe Office
                </div>
              </div>
            </div>

            {/* Hours */}
            <div className="cp-hours">
              <div className="cp-hours-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#c9a84c" }}>
                <Clock size={20} />
              </div>
              <div>
                <div className="cp-hours-title">Working Hours</div>
                <div className="cp-hours-row"><span>Mon – Sat</span><span>10:00 AM – 7:00 PM IST</span></div>
                <div className="cp-hours-row"><span>Sunday</span><span>Closed</span></div>
              </div>
            </div>

            {/* FAQ */}
            <div className="cp-faq">
              <div className="cp-faq-title">Frequently Asked Questions</div>
              {faqs.map((f, i) => (
                <div key={i} className={`cp-faq-item${openFaq === i ? " cp-faq-open" : ""}`} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <div className="cp-faq-q">
                    <span>{f.q}</span>
                    <span className="cp-faq-chevron">{openFaq === i ? "↑" : "↓"}</span>
                  </div>
                  {openFaq === i && <div className="cp-faq-a">{f.a}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="cp-right">
            <div className="cp-form-wrap">
              <div className="cp-form-orb"></div>
              <div className="cp-form-header">
                <div className="cp-form-title">Send Us a Message</div>
                <div className="cp-form-sub">Fill in the form and our team will reach out shortly.</div>
              </div>

              {submitted ? (
                <div className="cp-success">
                  <div className="cp-success-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#10b981" }}>
                    <CheckCircle2 size={48} />
                  </div>
                  <h3>Message Sent!</h3>
                  <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <button className="cp-btn-primary" onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", subject: "", service: "", message: "" }); }}>
                    Send Another →
                  </button>
                </div>
              ) : (
                <form className="cp-form" onSubmit={handleSubmit}>

                  <div className="cp-form-row">
                    <div className={`cp-field${focused === "name" ? " cp-field--focus" : ""}`}>
                      <label>Full Name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange}
                        placeholder="Your full name" required
                        onFocus={() => setFocused("name")} onBlur={() => setFocused("")} />
                    </div>
                    <div className={`cp-field${focused === "email" ? " cp-field--focus" : ""}`}>
                      <label>Email Address *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange}
                        placeholder="your@email.com" required
                        onFocus={() => setFocused("email")} onBlur={() => setFocused("")} />
                    </div>
                  </div>

                  <div className="cp-form-row">
                    <div className={`cp-field${focused === "phone" ? " cp-field--focus" : ""}`}>
                      <label>Phone Number</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                        placeholder="+91 XXXXXXXXXX"
                        onFocus={() => setFocused("phone")} onBlur={() => setFocused("")} />
                    </div>
                    <div className={`cp-field${focused === "service" ? " cp-field--focus" : ""}`}>
                      <label>Service Interested In</label>
                      <select name="service" value={formData.service} onChange={handleChange}
                        onFocus={() => setFocused("service")} onBlur={() => setFocused("")}>
                        <option value="">Select a service…</option>
                        <option>Recruitment Service</option>
                        <option>HR Payroll</option>
                        <option>HR Outsourcing</option>
                        <option>IT Service</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div className={`cp-field${focused === "subject" ? " cp-field--focus" : ""}`}>
                    <label>Subject *</label>
                    <input type="text" name="subject" value={formData.subject} onChange={handleChange}
                      placeholder="How can we help you?" required
                      onFocus={() => setFocused("subject")} onBlur={() => setFocused("")} />
                  </div>

                  <div className={`cp-field${focused === "message" ? " cp-field--focus" : ""}`}>
                    <label>Your Message *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange}
                      placeholder="Tell us about your requirements, goals, or questions…"
                      rows={5} required
                      onFocus={() => setFocused("message")} onBlur={() => setFocused("")}></textarea>
                  </div>

                  {error && (
                    <p style={{ color: "#ef4444", fontSize: "0.85rem", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                      ⚠ {error}
                    </p>
                  )}

                  <button type="submit" className="cp-btn-primary" disabled={sending} style={{ display: "inline-flex", alignItems: "center", gap: "8px", justifyContent: "center", opacity: sending ? 0.7 : 1, cursor: sending ? "not-allowed" : "pointer" }}>
                    <span>{sending ? "Sending…" : "Send Message"}</span> {!sending && <span>→</span>}
                  </button>

                  <p className="cp-form-note" style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <ShieldCheck size={14} style={{ color: "#10b981" }} /> Your information is safe with us and never shared with third parties.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* ── BOTTOM CTA BANNER ── */}
        <div className="cp-cta-banner">
          <div className="cp-cta-bg">
            <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200" alt="" />
            <div className="cp-cta-dim"></div>
          </div>
          <div className="cp-cta-inner">
            <div className="cp-cta-left">
              <div className="cp-cta-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#c9a84c" }}>
                <Send size={24} />
              </div>
              <div>
                <h3 className="cp-cta-h">Prefer a direct conversation?</h3>
                <p className="cp-cta-p">Book a free 20-minute discovery call — no pitch, no pressure, just clarity.</p>
              </div>
            </div>
            <div className="cp-cta-actions">
              <a href={`tel:${companyInfo.phone}`} className="cp-cta-btn-w" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <Phone size={14} /> Call India
              </a>
              <a href={`tel:${companyInfo.phone2}`} className="cp-cta-btn-g" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <Phone size={14} /> Call Globe
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Contact;
